import React from "react";
import { FileText, Loader2, Megaphone, Send } from "lucide-react";
import type { BroadcastFormState, BroadcastType } from "src/types/broadcasts";
import { NAVIGATION_TARGETS } from "src/lib/notificationRoutes";
import { Input, Select, Textarea } from "src/components/ui/Input";
import { SlabButton } from "src/components/ui/SlabButton";
import { PickerColors, SegmentedPicker } from "../SegmentedPicker";
import Collapsible from "../Collapsible";
import { motion } from "framer-motion";

interface Props {
  form: BroadcastFormState;
  onChange: (next: BroadcastFormState) => void;
  onSend: () => void;
  onSaveDraft?: () => void;
  sending: boolean;
  savingDraft?: boolean;
  /** True when this composer is loaded from an existing draft (changes the button copy). */
  editingDraft?: boolean;
  canManage: boolean;
  formError: string | null;
  sendError: string | null;
  sendSuccess: string | null;
  draftError?: string | null;
  draftSuccess?: string | null;
}

const TITLE_MAX = 200;
const BODY_MAX = 1000;

const TYPE_OPTIONS: { value: BroadcastType; label: string; desc: string }[] = [
  {
    value: "NEWS",
    label: "News",
    desc: "Town halls, education, merch drops",
  },
  {
    value: "PROMOTIONAL",
    label: "Promo",
    desc: "Marketing pushes, new surveys nearby",
  },
];

const TYPE_PICKER_COLORS: Record<BroadcastType, PickerColors> = {
  NEWS: {
    bg: "#EEF2FF",
    text: "#4F46E5",
    border: "#EEF2FF",
    shadow: "#C7D2FE",
  },
  PROMOTIONAL: {
    bg: "#FFE7B8",
    text: "#A45A00",
    border: "#FF9600",
    shadow: "#E07B00",
  },
};

const ENV_PICKER_UNSELECTED_COLOR: PickerColors = {
  bg: "#FFFFFF",
  text: "#777777",
  border: "#E5E5E5",
  shadow: "#D0D0D0",
};

const StepBadge: React.FC<{ n: number }> = ({ n }) => (
  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-indigo-500 bg-indigo-500 text-xs font-extrabold text-white shadow-[0_2px_0_#4338CA]">
    {n}
  </span>
);

export const BroadcastComposer: React.FC<Props> = ({
  form,
  onChange,
  onSend,
  onSaveDraft,
  sending,
  savingDraft = false,
  editingDraft = false,
  canManage,
  formError,
  sendError,
  sendSuccess,
  draftError,
  draftSuccess,
}) => {
  const update = <K extends keyof BroadcastFormState>(
    key: K,
    value: BroadcastFormState[K],
  ) => onChange({ ...form, [key]: value });

  const buildHeader = (
    step: number,
    title: string,
    desc?: string,
    status?: React.ReactNode,
  ) => (
    <div className="my-4 flex items-start gap-3">
      <StepBadge n={step} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-extrabold text-[#1F1F1F] sm:text-lg">
            {title}
          </h2>
          {status}
        </div>
        {desc && (
          <p className="mt-0.5 text-xs text-[#777777] sm:text-sm">{desc}</p>
        )}
      </div>
    </div>
  );

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (canManage && !sending) onSend();
      }}
    >
      {form.env === "prod" && (
        <motion.p
          initial={{ scale: 0.9, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 350, damping: 25 }}
          className="rounded-lg bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700"
        >
          Caution: Production sends to every live device. Please review your
          broadcast before sending.
        </motion.p>
      )}

      {/* Step 1 — Type */}
      <Collapsible header={buildHeader(1, "What kind of notification?", "")}>
        <SegmentedPicker
          className="px-4 py-4"
          layout="flexible"
          ariaLabel="Tap action"
          selectedColor={TYPE_PICKER_COLORS[form.type]}
          unselectedColor={ENV_PICKER_UNSELECTED_COLOR}
          options={TYPE_OPTIONS.map((opt) => opt.label)}
          value={
            TYPE_OPTIONS.find((opt) => opt.value === form.type)?.label || ""
          }
          onChange={(label) => {
            const chosen = TYPE_OPTIONS.find((opt) => opt.label === label);
            if (chosen) update("type", chosen.value);
          }}
        />
      </Collapsible>

      {/* Step 2 — Title + Body */}
      <Collapsible header={buildHeader(2, "Compose Broadcast", "")}>
        <div className="space-y-3 px-4 py-4">
          <label className="block">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-extrabold uppercase tracking-wide text-[#777777]">
                Title
              </span>
              <span className="text-[11px] text-[#9CA3AF]">
                {form.title.length}/{TITLE_MAX}
              </span>
            </div>
            <Input
              accent="teal"
              value={form.title}
              maxLength={TITLE_MAX}
              onChange={(e) => update("title", e.target.value)}
              placeholder="Vuer Town Hall — May 12"
            />
          </label>
          <label className="block">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-extrabold uppercase tracking-wide text-[#777777]">
                Body
              </span>
              <span className="text-[11px] text-[#9CA3AF]">
                {form.body.length}/{BODY_MAX}
              </span>
            </div>
            <Textarea
              accent="teal"
              value={form.body}
              maxLength={BODY_MAX}
              onChange={(e) => update("body", e.target.value)}
              placeholder="Join us live at 2pm CT."
            />
          </label>
        </div>
      </Collapsible>

      {/* Step 3 — Tap action */}
      <Collapsible
        header={buildHeader(
          3,
          "Where should taps go?",
          "Optional. Pick an in-app screen or an external URL.",
        )}
      >
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 px-4 py-4">
          {(
            [
              { value: "none", label: "No tap action" },
              { value: "NAVIGATION", label: "Open in-app screen" },
              { value: "URL_LAUNCH", label: "Open URL" },
            ] as const
          ).map((opt) => {
            const active = form.tapKind === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => update("tapKind", opt.value)}
                aria-pressed={active}
                className={`rounded-2xl border-2 px-3 py-3 text-sm font-extrabold transition-transform active:translate-y-0.5 ${
                  active
                    ? "border-indigo-500 bg-indigo-50 text-indigo-700 shadow-[0_4px_0_#C7D2FE]"
                    : "border-[#E5E5E5] bg-white text-[#6B7280] hover:border-[#D0D0D0]"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {form.tapKind === "NAVIGATION" && (
          <div className="mt-4 space-y-3 border-t-2 border-dashed border-[#E5E5E5] m-4">
            {/* <label className="block">
              <span className="mb-1 block text-xs font-extrabold uppercase tracking-wide text-[#777777]">
                Refresh event <span className="font-medium normal-case text-[#9CA3AF]">(optional)</span>
              </span>
              <Select
                value={form.navEvent}
                onChange={(e) => update("navEvent", e.target.value)}
              >
                <option value="">None</option>
                {NOTIFICATION_EVENTS.map((evt) => (
                  <option key={evt.value} value={evt.value}>
                    {evt.label} ({evt.value})
                  </option>
                ))}
              </Select>
            </label> */}
            <label className="block">
              <span className="mb-1 block text-xs font-extrabold uppercase tracking-wide text-[#777777]">
                Target screen
              </span>
              <Select
                value={form.navTarget}
                onChange={(e) => update("navTarget", e.target.value)}
              >
                <option value="">Pick a screen…</option>
                {NAVIGATION_TARGETS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label} ({t.value})
                  </option>
                ))}
              </Select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-extrabold uppercase tracking-wide text-[#777777]">
                Params JSON{" "}
                <span className="font-medium normal-case text-[#9CA3AF]">
                  (optional)
                </span>
              </span>
              <Textarea
                value={form.navParamsText}
                onChange={(e) => update("navParamsText", e.target.value)}
                placeholder='{"highlight_ids": ["vue-123"]}'
                className="font-mono text-xs"
                rows={3}
              />
            </label>
          </div>
        )}

        {form.tapKind === "URL_LAUNCH" && (
          <div className="mt-4 border-t-2 border-dashed border-[#E5E5E5] p-4">
            <label className="block">
              <span className="mb-1 block text-xs font-extrabold uppercase tracking-wide text-[#777777]">
                External URL
              </span>
              <Input
                type="url"
                value={form.url}
                onChange={(e) => update("url", e.target.value)}
                placeholder="https://ivueit.com/townhall"
                className="font-mono text-sm"
              />
            </label>
          </div>
        )}
      </Collapsible>

      {/* Send / Save draft */}
      <div className="rounded-[1.5rem] border-4 border-[#E5E5E5] bg-white p-5 shadow-[0_8px_0_#D0D0D0] sm:p-6">
        {formError && (
          <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">
            {formError}
          </div>
        )}
        {sendError && (
          <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">
            {sendError}
          </div>
        )}
        {draftError && (
          <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">
            {draftError}
          </div>
        )}
        {sendSuccess && (
          <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800">
            {sendSuccess}
          </div>
        )}
        {draftSuccess && (
          <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800">
            {draftSuccess}
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          {onSaveDraft && (
            <SlabButton
              type="button"
              variant="softOutline"
              disabled={!canManage || savingDraft || sending}
              onClick={onSaveDraft}
              className="w-full sm:w-auto sm:flex-1"
            >
              {savingDraft ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />{" "}
                  Saving…
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4" aria-hidden />
                  {editingDraft ? "Update draft" : "Save as draft"}
                </>
              )}
            </SlabButton>
          )}
          <SlabButton
            type="submit"
            variant="primary"
            disabled={!canManage || sending || savingDraft}
            className="w-full sm:w-auto sm:flex-1"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />{" "}
                Sending…
              </>
            ) : (
              <>
                {form.env === "prod" ? (
                  <Send className="h-4 w-4" aria-hidden />
                ) : (
                  <Megaphone className="h-4 w-4" aria-hidden />
                )}
                {form.env === "prod"
                  ? "Send to production"
                  : `Send to ${form.env}`}
              </>
            )}
          </SlabButton>
        </div>
        {!canManage && (
          <p className="mt-2 text-center text-xs text-[#777777]">
            You can view broadcasts but not send them.
          </p>
        )}
      </div>
    </form>
  );
};

export default BroadcastComposer;
